import { FC, SetStateAction, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import {
  companyMock,
  CustomFieldInstance,
  CustomFormEditor,
  CustomFormFieldType,
  CustomFormPage,
  CustomFormTemplate,
  CustomFormTemplatePageBackgroundFilePayload,
  CustomStatusesMap,
  DRAFT_CUSTOM_FORM_TEMPLATE,
  ErrorView,
  FileEntityUuidToFileMap,
  FpCompany,
  postMessageToParentWindows, Role,
  StatusWorkflow,
  Team,
  ToastService,
  usePreloaderContext,
  User,
  userMock,
  LocalizationContextProvider
} from '@flicent/fieldpulse-react-component-library-frontend';

import { DRAFT_URI_PARAM } from '@/constants';
import {
  createCustomFormTemplateRequest,
  getCustomFormSettingsDataRequest,
  getCustomFormTemplateCustomFieldInstancesRequest,
  getCustomFormTemplateDraftInitialDataRequest,
  getCustomFormTemplateRequest,
  updateCustomFormTemplatePageBackgroundRequest,
  updateCustomFormTemplateRequest,
  importCustomFormTemplateRequest,
  exportCustomFormTemplateRequest,
} from '@/services';
import {
  CustomFormTemplateOpenEntityEvent,
  CustomFormTemplateSetModifiedEvent,
  CustomFormTemplateUpdateEvent
} from '@/interfaces';
import { CustomFormTemplatePageUriParams } from '@/types';
import { constructCustomFormTemplatePageUrl } from '@/utils';
import { useCustomFormTranslation } from '../i18n';


const GlobalStyle = createGlobalStyle`
  html {
    // Prevent horizontal scroll when dragging a long width Custom Form Field in the Editor
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; // Firefox

    &::-webkit-scrollbar {
      display: none; // Safari and Chrome
    }

    // ====================================================================================
  }
`;


interface CustomFormTemplateEditorPageProps {
  onChangePageTitle: (newTitle: string) => void;
}

export const CustomFormTemplateEditorPage: FC<CustomFormTemplateEditorPageProps> = ({ onChangePageTitle }) => {
  const { id = DRAFT_URI_PARAM } = useParams<CustomFormTemplatePageUriParams>();

  const { t } = useCustomFormTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const preloaderContext = usePreloaderContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [formSettingsDataLoading, setFormSettingsDataLoading] = useState<boolean>(true);
  const [company, setCompany] = useState<FpCompany>(companyMock);
  const formTemplateInitialValue: CustomFormTemplate = DRAFT_CUSTOM_FORM_TEMPLATE;
  const [formTemplate, setFormTemplate] = useState<CustomFormTemplate>(formTemplateInitialValue);
  const [initialFormTemplate, setInitialFormTemplate] = useState<CustomFormTemplate>(formTemplateInitialValue);
  const [maxPageCount, setMaxPageCount] = useState<number>(1);
  const [user, setUser] = useState<User>(userMock);
  const [isCompanyCanEnableMoreTemplatesAvailableInOffline, setIsCompanyCanEnableMoreTemplatesAvailableInOffline] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [statusWorkflows, setStatusWorkflows] = useState<StatusWorkflow[]>([]);
  const [customStatusesMap, setCustomStatusesMap] = useState<CustomStatusesMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapFieldTypeToCustomFormInstances, setMapFieldTypeToCustomFormInstances] = useState<{ [key in CustomFormFieldType]?: CustomFieldInstance[] | undefined }>({});
  const [pageBackgroundFileEntityUrlToFileMap, setPageBackgroundFileEntityUrlToFileMap] = useState<FileEntityUuidToFileMap>({});

  const pageTitle = t('common.templatePageTitle', { name: loading ? t('common.loading') : formTemplate.name });

  const updateFormTemplateAndInitialFormTemplate = (formTemplate: SetStateAction<CustomFormTemplate>): void => {
    setFormTemplate(formTemplate);
    setInitialFormTemplate(formTemplate);
  };

  // Update Page Title
  useEffect(() => {
    onChangePageTitle(pageTitle);
  }, [pageTitle, onChangePageTitle]);

  // Fetch Data
  useEffect(() => {
    if (!token) {
      setError(t('customFormTemplateEditorPage.errors.brokenLinkNoToken'));
      return;
    }

    const handleStartLoading = () => {
      preloaderContext.showFullPagePreloader();
      setLoading(true);
    };

    const handleEndLoading = () => {
      preloaderContext.hideFullPagePreloader();
      setLoading(false);
    };

    const loadFormSettingsData = () => {
      getCustomFormSettingsDataRequest({ token })
        .then(({
                 is_company_can_enable_more_templates_available_in_offline,
                 roles,
                 teams,
                 users,
                 status_workflows,
                 custom_statuses_map
               }) => {
          setIsCompanyCanEnableMoreTemplatesAvailableInOffline(is_company_can_enable_more_templates_available_in_offline);
          setRoles(roles);
          setTeams(teams);
          setUsers(users);
          setStatusWorkflows(status_workflows);
          setCustomStatusesMap(custom_statuses_map);
        })
        .catch((error: Error) => {
          ToastService.middleScreenErrorToast(t('customFormTemplateEditorPage.errors.formSettingsFetchFailed', { error: error.message }));
        })
        .finally(() => {
          setFormSettingsDataLoading(false);
        });
    };

    if (id === DRAFT_URI_PARAM) {
      handleStartLoading();

      getCustomFormTemplateDraftInitialDataRequest({ token })
        .then(({ max_page_count, company, user }) => {
          setMaxPageCount(max_page_count);
          setUser(user);
          setCompany(company);
          updateFormTemplateAndInitialFormTemplate(DRAFT_CUSTOM_FORM_TEMPLATE);
        })
        .catch((error: Error) => {
          ToastService.middleScreenErrorToast(t('customFormTemplateEditorPage.errors.draftInitialDataFetchFailed', { error: error.message }));
        })
        .finally(handleEndLoading);

      loadFormSettingsData();
    } else {
      if (formTemplate.id !== parseInt(id)) {
        handleStartLoading();

        getCustomFormTemplateRequest({ id: parseInt(id), token })
          .then(({ max_page_count, company, user, template }) => {
            setMaxPageCount(max_page_count);
            setUser(user);
            setCompany(company);
            updateFormTemplateAndInitialFormTemplate(template);
            loadFormSettingsData();
          })
          .catch((error: Error) => {
            setError(error.message);
          })
          .finally(handleEndLoading);
      }
    }
  }, [id]);

  const navigateToCustomFormTemplateById = (id: number) => {
    const route = constructCustomFormTemplatePageUrl({ id: id.toString() })
      + '' + location.search
      + '' + location.hash;

    navigate(route, {
      replace: true,
    });
  };

  const openWorkflowStatusesPage = (): void => {
    const postMessageEvent: CustomFormTemplateOpenEntityEvent = {
      type: 'fp-form-template',
      action: 'open',
      data: {
        entity: 'status-workflow',
        entityId: null
      }
    };

    postMessageToParentWindows(postMessageEvent);
  };

  const saveForm = async (
    templateWithoutValuesAndBackgrounds: CustomFormTemplate,
    pageBackgroundFilesPayloads: CustomFormTemplatePageBackgroundFilePayload[],
  ) => {
    const isCreate: boolean = id === DRAFT_URI_PARAM

    try {
      // 1. Create or Update Custom Form Template
      let newTemplate: CustomFormTemplate
      let newIsCompanyCanEnableMoreTemplatesAvailableInOffline: boolean;
      if (isCreate) {
        const response = await createCustomFormTemplateRequest({
          token: token!,
          formTemplate: templateWithoutValuesAndBackgrounds,
        })
        newTemplate = response.template;
        newIsCompanyCanEnableMoreTemplatesAvailableInOffline = response.is_company_can_enable_more_templates_available_in_offline;
      } else {
        const response = await updateCustomFormTemplateRequest({
          token: token!,
          formTemplate: templateWithoutValuesAndBackgrounds,
        })
        newTemplate = response.template;
        newIsCompanyCanEnableMoreTemplatesAvailableInOffline = response.is_company_can_enable_more_templates_available_in_offline;
      }

      // 2. Update state with new template,
      // but replace Page Background Files with the current state ones,
      // because they are about to be uploaded in the next step, and we want user to still see them
      const newTemplateWithOldBackgrounds: CustomFormTemplate = {
        ...newTemplate,
        pages: newTemplate.pages.map((page: CustomFormPage) => ({
          ...page,
          background_file: formTemplate.pages.find((p) => p.uuid === page.uuid)?.background_file || null,
        })),
      }

      updateFormTemplateAndInitialFormTemplate(newTemplateWithOldBackgrounds);

      // 3. Update state with new isCompanyCanEnableMoreTemplatesAvailableInOffline
      setIsCompanyCanEnableMoreTemplatesAvailableInOffline(newIsCompanyCanEnableMoreTemplatesAvailableInOffline);

      // 4. Upload Page Background Files, and update state with them
      await Promise.all(
        pageBackgroundFilesPayloads.map(async (payload) => {
          const fileEntity = await updateCustomFormTemplatePageBackgroundRequest({
            token: token!,
            formTemplateId: newTemplate.id!,
            payload,
          })

          updateFormTemplateAndInitialFormTemplate((prevTemplate: CustomFormTemplate) => ({
            ...prevTemplate,
            pages: prevTemplate.pages.map((page: CustomFormPage) => {
              if (page.uuid !== payload.pageUuid) {
                return page
              }

              return {
                ...page,
                background_file: fileEntity,
              }
            }),
          }));
        })
      )

      // 5. Toast
      ToastService.middleScreenToast(isCreate ? t('common.created') : t('common.saved'));

      // 6. Cleanup
      setPageBackgroundFileEntityUrlToFileMap({});

      // 7. Notify Parent Windows
      const updatePostMessageEvent: CustomFormTemplateUpdateEvent = {
        type: 'fp-form-template',
        action: isCreate ? 'create' : 'edit',
        data: newTemplate,
      };
      postMessageToParentWindows(updatePostMessageEvent);

      // 8. Navigate to new template page (for Development)
      if (isCreate) {
        navigateToCustomFormTemplateById(newTemplate.id!);
      }
    } catch (error: any) {
      ToastService.middleScreenErrorToast(t('customFormTemplateEditorPage.errors.unexpectedSaveError', { error: error.message }));
    }
  };

  const fetchRelatedEntityFieldCustomFieldInstances = (relatedFieldType: CustomFormFieldType): Promise<CustomFieldInstance[]> => {
    if (mapFieldTypeToCustomFormInstances[relatedFieldType]) {
      return Promise.resolve(mapFieldTypeToCustomFormInstances[relatedFieldType]!);
    }

    return getCustomFormTemplateCustomFieldInstancesRequest({
      token: token!,
      relatedFieldType
    }).then(({ custom_field_instances }) => {
      setMapFieldTypeToCustomFormInstances((prevValue) => ({
        ...prevValue,
        [relatedFieldType]: custom_field_instances
      }));
      return custom_field_instances;
    });
  };

  const handleChangeModified = (newModified: boolean) => {
    const modifiedPostMessageEvent: CustomFormTemplateSetModifiedEvent = {
      type: 'fp-form-template',
      action: 'set-modified',
      data: newModified,
    };
    postMessageToParentWindows(modifiedPostMessageEvent);
  };

  const handleGetTemplateWithTemplateImportJson = (templateImportJson: string) => {
    return importCustomFormTemplateRequest({ token: token!, templateImportJson });
  };

  const handleGetTemplateImportJson = (template: CustomFormTemplate) => {
    return exportCustomFormTemplateRequest({
      token: token!,
      template
    }).then(({ template_import_json }) => template_import_json);
  };

  if (!token) {
    return <Navigate to="/not-allowed"/>;
  }

  if (error) {
    return <ErrorView errorDetails={ error }/>;
  }

  return (
    <>
      <GlobalStyle/>
      <LocalizationContextProvider maintenanceAgreementName={ company?.maintenance_agreement_name }
                                   maintenanceAgreementNamePlural={ company?.maintenance_agreement_name_plural }>
        <CustomFormEditor
          isDraft={ id === DRAFT_URI_PARAM }
          loading={ loading }
          isCompanyCanEnableMoreTemplatesAvailableInOffline={ isCompanyCanEnableMoreTemplatesAvailableInOffline }
          maxPageCount={ maxPageCount }
          customStatusesMap={ customStatusesMap }
          pageBackgroundFileEntityUrlToFileMap={ pageBackgroundFileEntityUrlToFileMap }
          setPageBackgroundFileEntityUrlToFileMap={ setPageBackgroundFileEntityUrlToFileMap }
          company={ company }
          formSettingsDataLoading={ formSettingsDataLoading }
          initialFormTemplate={ initialFormTemplate }
          formTemplate={ formTemplate }
          onChangeFormTemplate={ setFormTemplate }
          user={ user }
          roles={ roles }
          teams={ teams }
          users={ users }
          statusWorkflows={ statusWorkflows }
          onFormTemplateSave={ saveForm }
          onClickOpenWorkflowStatusesPage={ openWorkflowStatusesPage }
          onShouldFetchRelatedEntityFieldCustomFieldInstances={ fetchRelatedEntityFieldCustomFieldInstances }
          onChangeModified={ handleChangeModified }
          onGetTemplateWithTemplateImportJson={ handleGetTemplateWithTemplateImportJson }
          onGetTemplateImportJson={ handleGetTemplateImportJson }
        />
      </LocalizationContextProvider>
    </>
  );
};
